import { create } from 'zustand';
import type { EventEnvelope } from '@contracts/events';
import type { SRAlert, SRNotice, SROutcomeEvent, SRSnapshot, SRStatus } from '@contracts/sr';

const MAX_ALERTS = 100;
const MAX_NOTICES = 100;

/** Live state of the support/resistance system — kept apart from the signals store. */
export interface SRState {
  status: SRStatus | null;
  alerts: SRAlert[];
  notices: SRNotice[];
  handleEvent: (env: EventEnvelope) => void;
  reset: () => void;
}

function upsert(list: SRAlert[], a: SRAlert): SRAlert[] {
  return [a, ...list.filter((x) => x.id !== a.id)]
    .sort((x, y) => y.createdAt.localeCompare(x.createdAt))
    .slice(0, MAX_ALERTS);
}

export const useSR = create<SRState>((set) => ({
  status: null,
  alerts: [],
  notices: [],
  reset: () => set({ status: null, alerts: [], notices: [] }),
  handleEvent: (env) => {
    const p = env.payload as unknown;
    switch (env.type) {
      case 'sr.snapshot': {
        const snap = p as SRSnapshot;
        set({ status: snap.status, alerts: snap.alerts.slice(0, MAX_ALERTS), notices: snap.notices });
        break;
      }
      case 'sr.status':
        set((s) => ({ status: { ...(p as SRStatus), channels: s.status?.channels } }));
        break;
      case 'sr.notice':
        set((s) => ({
          notices: [p as SRNotice, ...s.notices.filter((n) => n.id !== (p as SRNotice).id)].slice(0, MAX_NOTICES),
        }));
        break;
      case 'sr.alert':
        set((s) => ({ alerts: upsert(s.alerts, p as SRAlert) }));
        break;
      case 'sr.outcome':
        set((s) => ({ alerts: upsert(s.alerts, (p as SROutcomeEvent).alert) }));
        break;
      default:
        break;
    }
  },
}));
